import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiConfigService } from '../api/api-config.service';
import { TenantService } from '../services/tenant.service';
import { ThemeService } from '../services/theme.service';

export enum UserTheme {
  Light = 1,
  Dark = 2
}

export enum UserGenre {
  Male = 1,
  Female = 2
}

const ROLE_CLAIM = 'role';

/**
 * Papéis que administram o tenant ativo (contexto do cliente, não o Hub global). Exportado
 * para a tela de vínculo OAuth (#814) filtrar `CustomerDTO.userRole` com a MESMA régua de
 * `isTenantAdmin` — D8 (spec de OAuth) exige "só administrador", e duas listas de papéis
 * divergindo seria o próximo #818.
 */
export const TENANT_ADMIN_ROLES = ['Acesso-Admin', 'Admin', 'Owner'];

export interface UserLogin {
  user: string;
  password: string;
  twoFactorCode?: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface ConfirmResetPasswordRequest {
  hash: string;
  new_password: string;
}

export interface Token {
  authenticated: boolean;
  requires_two_factor: boolean;
  access_token: string | null;
  refresh_token: string | null;
  date_activated: string;
  date_expiration_access_token: string;
  date_expiration_refresh_token: string;
  message: string | null;
}

export interface TokenModel {
  access_token: string;
  refresh_token: string;
}

export interface UserDTO {
  id: string;
  name?: string | null;
  email?: string | null;
  logo?: string | null;
  password?: string | null;
  genre?: UserGenre | null;
  crm?: string | null;
  phone?: string | null;
  role?: string | null;
  allow: boolean;
  accept: boolean;
  availability: boolean;
  recoveryEmail?: string | null;
  theme?: UserTheme | null;
  language?: string | null;
  emailNotifications?: boolean;
  whatsAppNotifications?: boolean;
  function?: UserFunctionDTO;
}

export interface UserFunctionDTO {
  id: string;
  name: string | null;
}

export interface Functionality {
  id: string;
  code: string;
  name: string;
  moduleId: string | null;
  parentId: string | null;
  subs: Functionality[];
}

/**
 * Erro lançado quando o refresh falha de forma definitiva (resposta sem
 * access_token, ou refresh_token já rotacionado/expirado). Sinaliza ao
 * errorInterceptor que deve executar logout, ao contrário de erros de rede.
 */
export class RefreshFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefreshFailedError';
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private api = inject(ApiConfigService);
  private tenantService = inject(TenantService);
  private themeService = inject(ThemeService);

  private _user = signal<UserDTO | null>(null);
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._user() !== null);

  private _permissions = signal<Functionality[]>([]);
  readonly permissions = this._permissions.asReadonly();

  // Roles extraídas do JWT e mantidas reativas via signal.
  // Inicializado do localStorage para sobreviver a F5.
  private _roles = signal<string[]>(this.decodeRolesFromToken(localStorage.getItem('hub_access_token')));
  readonly roles = this._roles.asReadonly();
  readonly isAdmin = computed(() => this._roles().includes('Acesso-Admin'));

  /**
   * Administrador do TENANT ativo — quem pode ver/usar a seção Administração da
   * sidebar do cliente e as rotas `/:tenantId/configuracoes/**`.
   *
   * Reutiliza a regra já estabelecida em `ConfiguracoesComponent`:
   * `Acesso-Admin` (admin global do Hub, superset) OU `Admin`/`Owner` do tenant.
   * NÃO confundir com `isAdmin` (Administração GLOBAL do Hub, `/admin/**`).
   *
   * Fail-closed: enquanto o backend não expõe permissões administrativas
   * granulares por tenant, o papel administrativo é a fonte de verdade — nunca
   * liberar para papéis comuns (`User`, `Support`).
   */
  readonly isTenantAdmin = computed(() =>
    this._roles().some(r => TENANT_ADMIN_ROLES.includes(r)),
  );

  // Refresh em andamento NESTA aba (single-flight). Garante que múltiplas
  // requisições que tomam 401 simultaneamente compartilhem o mesmo refresh.
  private refreshInFlight: Promise<string> | null = null;

  login(credentials: UserLogin) {
    return this.http.post<Token>(this.api.url('/api/login'), credentials).pipe(
      tap(token => {
        if (token.authenticated && !token.requires_two_factor) {
          this.storeTokens(token);
        }
      })
    );
  }

  loadCurrentUser() {
    return this.http.get<UserDTO>(this.api.url('/api/login')).pipe(
      tap(user => {
        this._user.set(user);
        this.applyUserTheme(user.theme);
      })
    );
  }

  private applyUserTheme(theme: UserTheme | null | undefined): void {
    if (theme === UserTheme.Dark) {
      this.themeService.setTheme('dark');
    } else if (theme === UserTheme.Light) {
      this.themeService.setTheme('light');
    }
    // null/undefined → mantém a preferência local (localStorage / prefers-color-scheme)
  }

  loadPermissions() {
    return this.http.get<Functionality[]>(this.api.url('/api/login/permissions')).pipe(
      tap(perms => this._permissions.set(perms))
    );
  }

  getRoles(): string[] {
    return this._roles();
  }

  hasRole(role: string): boolean {
    return this._roles().includes(role);
  }

  hasPermission(code: string): boolean {
    const check = (items: Functionality[]): boolean =>
      items.some(f => f.code === code || check(f.subs));
    return this._roles().includes('Acesso-Admin') || check(this._permissions());
  }

  logout(): void {
    localStorage.removeItem('hub_access_token');
    localStorage.removeItem('hub_refresh_token');
    this.tenantService.clearTenant();
    this._user.set(null);
    this._permissions.set([]);
    this._roles.set([]);
    this.router.navigate(['/auth/login']);
  }

  refreshToken() {
    const payload: TokenModel = {
      access_token: localStorage.getItem('hub_access_token') ?? '',
      refresh_token: localStorage.getItem('hub_refresh_token') ?? ''
    };
    return this.http.post<Token>(this.api.url('/api/login/refresh-token'), payload).pipe(
      tap(token => this.storeTokens(token))
    );
  }

  /**
   * Renova o access token de forma coordenada e retorna o token válido a ser
   * usado na requisição que falhou com 401.
   *
   * Coordenação em duas camadas:
   * 1. Single-flight NA ABA: requisições paralelas compartilham a mesma Promise.
   * 2. Lock ENTRE ABAS (Web Locks API): apenas uma aba do mesmo navegador
   *    executa o POST /refresh-token por vez. Como o backend rotaciona o
   *    refresh_token (reusar o anterior retorna 403), sem essa coordenação
   *    duas abas renovariam em corrida e uma derrubaria a sessão da outra.
   *
   * @param failedToken access token que estava na requisição que tomou 401.
   *   Se, ao obter o lock, o token no localStorage já for outro, significa que
   *   outra aba renovou — reutilizamos esse token novo em vez de renovar de novo.
   */
  refreshAccessToken(failedToken: string | null): Promise<string> {
    this.refreshInFlight ??= this.runRefreshWithLock(failedToken).finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private runRefreshWithLock(failedToken: string | null): Promise<string> {
    const exec = async (): Promise<string> => {
      // Outra aba pode ter renovado enquanto aguardávamos o lock.
      const current = localStorage.getItem('hub_access_token');
      if (current && failedToken && current !== failedToken) {
        this._roles.set(this.decodeRolesFromToken(current));
        return current;
      }

      const token = await firstValueFrom(this.refreshToken());
      if (!token?.access_token) {
        throw new RefreshFailedError('refresh-token retornou sem access_token');
      }
      return token.access_token;
    };

    // Web Locks API serializa o refresh entre abas do mesmo navegador.
    // Fallback (navegadores sem suporte): single-flight só na aba.
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request('hub_token_refresh', exec);
    }
    return exec();
  }

  getAccessToken(): string | null {
    return localStorage.getItem('hub_access_token');
  }

  resetPassword(email: string) {
    return this.http.post<void>(this.api.url('/api/login/reset-password'), { email });
  }

  confirmResetPassword(payload: ConfirmResetPasswordRequest) {
    return this.http.post<void>(this.api.url('/api/login/reset-password'), payload);
  }

  updateUser(user: UserDTO): void {
    this._user.set(user);
  }

  private storeTokens(token: Token): void {
    if (!token.access_token || !token.refresh_token) return;
    localStorage.setItem('hub_access_token', token.access_token);
    localStorage.setItem('hub_refresh_token', token.refresh_token);
    this._roles.set(this.decodeRolesFromToken(token.access_token));
  }

  private decodeRolesFromToken(token: string | null): string[] {
    if (!token) return [];
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const claim = payload[ROLE_CLAIM];
      return Array.isArray(claim) ? claim : claim ? [claim] : [];
    } catch {
      return [];
    }
  }
}
